import React, { useState, useEffect, Fragment } from "react";
import api from "api";
import ApiCaller from "api/apiCaller";
import Cookies from "js-cookie";
import {
	Button,
	Row,
	Col
} from "reactstrap";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "./index.css";

import { Dashboard, ProjectFilter } from "model/dashboard";
import { UserInfo } from "model/user";
import Loading from "components/Loading";
import { startOfMonth, endOfMonth, startOfYear, subMonths, subDays, format } from "date-fns";
import * as utils from "resources/utils"

import "react-datepicker/dist/react-datepicker.css";

export default function DashboardCompany() {
	let timeRangeOptions = [
		{"value": "current_month", "label": "Current month"},
		{"value": "last_month", "label": "Last month"},
		{"value": "last_30_days", "label": "Last 30 days"},
		{"value": "last_90_days", "label": "Last 90 days"},
		{"value": "ytd", "label": "Year to date"},
		{"value": "custom", "label": "Custom range"}
	];
	let dateFormat = 'yyyy-MM-dd';

	const companyId: any = api.getCurrentCompanyId();
	const baseRoute = 'monitoring/dashboard'
	const [currentUser, setCurrentUser] = useState<UserInfo | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const canEditDashboard = currentUser?.is_admin || currentUser?.is_company_admin;
	const [dashboard, setDashboard] = useState<Dashboard>({});
	const [filterProject, setFilterProject] = useState<Array<ProjectFilter>>([]);
	const [defaultFilterProjectValue, setDefaultFilterProjectValue] = useState<any>({});
	const [selectedProject, setSelectedProject] = useState<string>('');
	const [selectedTimeRange, setSelectedTimeRange] = useState<string>('current_month');
	const [startDate, setStartDate] = useState(startOfMonth(new Date()));
	const [endDate, setEndDate] = useState(new Date());

	const initData = async () => {
		const user = api.getCurrentUser();
		setCurrentUser(user);
	};

	const getDashboard = async () => {
		if (Cookies.get('datasource_id') === 'undefined') {
			Cookies.remove('datasource_id');
		}
		const datasourceId = selectedProject ? selectedProject : (Cookies.get('datasource_id') ? Cookies.get('datasource_id') : '');
		const startDateParam = format(startDate, dateFormat);
		const endDateParam = format(endDate, dateFormat);
		const res:any = await ApiCaller.get(
			`${baseRoute}?company_id=${companyId}` +
			`&datasource_id=${datasourceId}` +
			`&start_date=${startDateParam}&end_date=${endDateParam}`
		);
		const data: Dashboard = res?.data;
		if (res?.status === "ok") {
			setDashboard({
				...dashboard,
				...data,
			});

			if (data.project_filter) {
				setFilterProject(data.project_filter);
				const defaultValue:any = data.project_filter.find((item) => {
					return item.value === data.datasource_id
				})
				setDefaultFilterProjectValue(defaultValue);
			}
			setLoading(false);
		} else {
			setLoading(false);
		}
	};

	const handleProjectChange = (selectedOption:any) => {
		if (selectedOption) {
			setSelectedProject(selectedOption.value);
			utils.default.setCookies('datasource_id', selectedOption.value)
		}
	};

	const handleTimeRangeChange = (selectedOption:any) => {
		if (selectedOption) {
			setSelectedTimeRange(selectedOption.value);
			const currentDate = new Date();
			if (selectedOption.value === 'current_month') {
				setStartDate(startOfMonth(currentDate));
				setEndDate(endOfMonth(currentDate));
			} else if (selectedOption.value === 'last_month') {
				setStartDate(startOfMonth(subMonths(currentDate, 1)));
				setEndDate(endOfMonth(subMonths(currentDate, 1)));
			} else if (selectedOption.value === 'last_30_days') {
				setStartDate(subDays(currentDate, 30));
				setEndDate(currentDate);
			} else if (selectedOption.value === 'last_90_days') {
				setStartDate(subDays(currentDate, 90));
				setEndDate(currentDate);
			} else if (selectedOption.value === 'ytd') {
				setStartDate(startOfYear(currentDate));
				setEndDate(currentDate);
			} else if (selectedOption.value === 'custom') {
				setStartDate(startOfMonth(currentDate));
				setEndDate(currentDate);
			}
		} else {
			setSelectedTimeRange('')
		}
	}

	const goToDashboardSetup = () => {
		window.location.href = `/${baseRoute}/setup/create/1`;
	};

	const goToDashboardEdit = () => {
		window.location.href = `/${baseRoute}/setup/edit/${dashboard.id}`;
	}

	useEffect(() => {
		initData();
	}, []);

	useEffect(() => {
		getDashboard();
	}, [selectedProject, selectedTimeRange, startDate, endDate])

	if (loading) return <Loading />;

	return (
		<div>
			<div className="dashboard__control-bar">
				{canEditDashboard &&
					<Col xs={12}>
						<div>
							<Fragment>
								<Button
									color="primary"
									className="dashboard__control-buttons"
									onClick={goToDashboardSetup}
									size="sm"
								>
									<span className="material-icons">addchart</span>
									<span>Setup New Dashboard</span>
								</Button>
								{dashboard?.id &&
									<Button
										color="primary"
										className="dashboard__control-buttons"
										onClick={goToDashboardEdit}
										size="sm"
									>
										<span className="material-icons">edit</span>
										<span>Edit Dashboard</span>
									</Button>
								}
							</Fragment>
						</div>
					</Col>
				}

			{/* Filters */}
			<Row>
				<Col xs={4} className='dashboard__filter'>
					<Select
						options={filterProject}
						onChange={(selectedOption: any) => {handleProjectChange(selectedOption)}}
						placeholder='Project'
						defaultValue={defaultFilterProjectValue}
					/>
				</Col>
				<Col xs={3} className='dashboard__filter'>
					<Select
						options={timeRangeOptions}
						onChange={(selectedOption: any) => {handleTimeRangeChange(selectedOption)}}
						placeholder='Time range'
						defaultValue={timeRangeOptions[0]}
					/>
				</Col>
			</Row>
			{selectedTimeRange === 'custom' &&
				<Row>
					<Col xs={4} className='dashboard__filter'>
						Start range
						<DatePicker selected={startDate} onChange={date => date && setStartDate(date)} />
					</Col>
					<Col xs={4} className='dashboard__filter'>
						End range
						<DatePicker selected={endDate} onChange={date => date && setEndDate(date)} />
					</Col>
				</Row>
			}
		</div>

		{/* Dashboard Visualization */}
		{dashboard?.iframe_url ?
			<iframe
				src={dashboard.iframe_url}
				frameBorder="0"
				allowTransparency
				className="metabase-iframe"
			/> :
			<h3> No dashboard </h3>
		}
		</div>
	)
}

